import { usePermissions } from './usePermissions';

// Mapeamento de páginas para suas permissões necessárias - Nova estrutura
const PAGE_PERMISSIONS = {
  // Dashboard principal
  '/dashboard': ['dashboard.view'],
  
  // Módulos específicos
  '/comercial': ['comercial.view', 'comercial.ranking.view', 'comercial.minhas_vendas.view'],
  '/financeiro': ['financeiro.view'],
  '/contratos': ['contratos.view'],
  '/operacional': ['operacional.view'],
  '/estoque': ['estoque.view'],
  '/infraestrutura': ['infra.view'],
  '/cobranca': ['cobranca.view'],
  
  // Departamento Pessoal
  '/departamento-pessoal': ['dp.view'],

  // Administrativo
  '/administrativo': ['administrativo.view'],
  '/gestao-solicitacoes': ['solicitacoes.view'],
  '/gerenciar-inscricoes': ['solicitacoes.view'],
  '/configuracoes': ['config.usuarios.view', 'config.preset.view'],
  
  // Perfil
  '/perfil': [], // Sempre permitido para usuários logados
};

export const usePagePermissions = () => {
  const { hasPermission, hasAnyPermission, getUserPermissions } = usePermissions();

  const getDirectoryItems = () => {
    return [
      {
        path: '/financeiro',
        label: 'Financeiro',
        icon: 'BarChart3',
        color: 'bg-green-600',
        permissions: ['financeiro.view'],
        visible: hasAnyPermission(['financeiro.view'])
      },
      {
        path: '/contratos',
        label: 'Contratos',
        icon: 'FileText',
        color: 'bg-gray-600',
        permissions: ['contratos.view'],
        visible: hasAnyPermission(['contratos.view'])
      },
      {
        path: '/comercial',
        label: 'Comercial',
        icon: 'Users',
        color: 'bg-purple-600',
        permissions: ['comercial.view', 'comercial.ranking.view', 'comercial.minhas_vendas.view'],
        visible: hasAnyPermission(['comercial.view', 'comercial.ranking.view', 'comercial.minhas_vendas.view'])
      },
      {
        path: '/operacional',
        label: 'Operacional',
        icon: 'Settings',
        color: 'bg-orange-600',
        permissions: ['operacional.view', 'operacional.agendamentos.view', 'operacional.edit'],
        visible: hasAnyPermission(['operacional.view', 'operacional.agendamentos.view', 'operacional.edit'])
      },
      {
        path: '/estoque',
        label: 'Estoque',
        icon: 'Package',
        color: 'bg-emerald-600',
        permissions: ['estoque.view'],
        visible: hasAnyPermission(['estoque.view'])
      },
      {
        path: '/infraestrutura',
        label: 'Infraestrutura',
        icon: 'Shield',
        color: 'bg-slate-600',
        permissions: ['infra.view'],
        visible: hasAnyPermission(['infra.view'])
      },
      {
        path: '/cobranca',
        label: 'Cobrança',
        icon: 'HandCoins',
        color: 'bg-amber-600',
        permissions: ['cobranca.view'],
        visible: hasAnyPermission(['cobranca.view'])
      },
      {
        path: '/departamento-pessoal',
        label: 'Depto. Pessoal',
        icon: 'Users',
        color: 'bg-teal-600',
        permissions: ['dp.view'],
        visible: hasAnyPermission(['dp.view'])
      },
      {
        path: '/administrativo',
        label: 'Administrativo',
        icon: 'ClipboardList',
        color: 'bg-indigo-600',
        permissions: ['administrativo.view'],
        visible: hasAnyPermission(['administrativo.view'])
      },
      {
        path: '/automacoes',
        label: 'Automações',
        icon: 'Zap',
        color: 'bg-cyan-600',
        permissions: ['automacoes.view'],
        visible: hasAnyPermission(['automacoes.view'])
      }
    ].filter((item) => item.visible);
  };

  const canAccessPage = (pagePath) => {
    const requiredPermissions = PAGE_PERMISSIONS[pagePath] || [];
    
    // Se não há permissões definidas, permitir acesso
    if (requiredPermissions.length === 0) {
      return true;
    }
    
    return hasAnyPermission(requiredPermissions);
  };

  const getPagePermissions = (pagePath) => {
    return PAGE_PERMISSIONS[pagePath] || [];
  };

  const getAccessiblePages = () => {
    const accessiblePages = [];

    Object.entries(PAGE_PERMISSIONS).forEach(([page, permissions]) => {
      if (permissions.length === 0 || hasAnyPermission(permissions)) {
        accessiblePages.push(page);
      }
    });

    return accessiblePages;
  };

  const getNavigationItems = () => {
    return getDirectoryItems();
  };

  const getCurrentDirectory = (pathname = '') => {
    return getDirectoryItems().find((item) => pathname === item.path || pathname.startsWith(`${item.path}/`)) || null;
  };

  const isGlobalRoute = (pathname = '') => {
    return !getCurrentDirectory(pathname);
  };

  return {
    canAccessPage,
    getPagePermissions,
    getAccessiblePages,
    getNavigationItems,
    getDirectoryItems,
    getCurrentDirectory,
    isGlobalRoute,
    hasPermission,
    hasAnyPermission,
    getUserPermissions
  };
};
